import React, { useState, useEffect } from "react";
import {
  QrCode,
  Save,
  Info,
  Settings as SettingsIcon,
  CheckCircle2,
  Volume2,
  VolumeX,
  Smartphone,
  Usb,
  Bluetooth,
  Plus,
  Trash2,
  Check,
  Server,
  Layers,
  RefreshCw,
  Cable,
  Edit2,
  X,
  Wifi,
  WifiOff,
} from "lucide-react";
import { SCANNER_MODES, getScannerConfig, setScannerConfig } from "../utils/scannerConfig";
import { useNotification } from "../contexts/NotificationContext";
import { Capacitor } from "@capacitor/core";

// ─── Device Detection Helpers ──────────────────────────────────────────

/** Check if WebHID API is available (Chrome 89+) */
const isWebHIDSupported = () =>
  typeof navigator !== "undefined" && !!navigator.hid;

/** Check if Web Bluetooth API is available */
const isWebBluetoothSupported = () =>
  typeof navigator !== "undefined" && !!navigator.bluetooth;

/** Check if Cordova bluetoothSerial plugin available (APK) */
const isCordovaBTAvailable = () =>
  typeof window !== "undefined" && !!window.bluetoothSerial;

/** Check if running inside Capacitor */
const isCapacitorAvailable = () =>
  (typeof window !== "undefined" && !!window.Capacitor) || Capacitor.isNativePlatform();

// ─── Component ─────────────────────────────────────────────────────────

export default function ScannerConfigTab() {
  const { showSuccess, showError, showInfo } = useNotification();
  const [config, setConfig] = useState(() => getScannerConfig());

  // Scanning / Discovery state
  const [isScanning, setIsScanning] = useState(false);
  const [discoveredDevices, setDiscoveredDevices] = useState([]);
  const [scanStatus, setScanStatus] = useState("Belum Melakukan Pencarian");
  const [connectingDeviceId, setConnectingDeviceId] = useState(null);

  // Device naming
  const [editingDeviceId, setEditingDeviceId] = useState(null);
  const [aliasInput, setAliasInput] = useState("");



  // Add device form
  const [isAddManualOpen, setIsAddManualOpen] = useState(false);
  const [manualDevName, setManualDevName] = useState("");
  const [manualDevMode, setManualDevMode] = useState("usb_hid");



  // ─── Save Config ──────────────────────────────────────────────────

  const handleSave = (updatedConfig = config) => {
    setScannerConfig(updatedConfig);
    setConfig(updatedConfig);
    showSuccess("Konfigurasi scanner berhasil disimpan!");
  };

  // ─── Device Discovery ─────────────────────────────────────────────

  const buildSavedDevice = (device) => ({
    id: device.id,
    name: device.name,
    type: device.type,
    mode: device.mode,
    status: device.connected ? "Terdeteksi & Dipasangkan" : "Dipasangkan",
    isDefault: false,
    lastUsed: "Baru Ditambahkan",
    source: device.source,
    vendorId: device.vendorId,
    productId: device.productId,
    address: device.address,
  });

  const persistDiscoveredDevices = (devices) => {
    const savedDevices = config.savedDevices || [];
    const newDevices = devices
      .filter((device) => !savedDevices.some((saved) => saved.id === device.id))
      .map(buildSavedDevice);

    if (newDevices.length === 0) return 0;

    const updatedConfig = {
      ...config,
      savedDevices: [...savedDevices, ...newDevices],
    };
    setScannerConfig(updatedConfig);
    setConfig(updatedConfig);
    return newDevices.length;
  };

  /**
   * Scan for real devices and persist every detected device locally.
   * Local storage is retained by the APK WebView, so saved devices remain
   * available after leaving the page or reopening the application.
   */
  const scanForDevices = async () => {
    setIsScanning(true);
    setScanStatus("Memindai perangkat yang terhubung ke sistem...");
    const found = [];

    // ── 1. WebHID (USB Scanner / Bluetooth HID) ──
    if (isWebHIDSupported()) {
      try {
        setScanStatus("Memeriksa perangkat HID yang sudah dipasangkan...");
        const pairedHidDevices = await navigator.hid.getDevices();
        for (const dev of pairedHidDevices) {
          found.push({
            id: `hid-${dev.vendorId}-${dev.productId}`,
            name: dev.productName || `HID Device (${dev.vendorId}:${dev.productId})`,
            type: "USB / Bluetooth HID",
            mode: "usb_hid",
            vendorId: dev.vendorId,
            productId: dev.productId,
            connected: dev.opened,
            source: "WebHID",
          });
        }
      } catch (err) {
        console.warn("WebHID getDevices error:", err);
      }
    }

    // ── 2. Cordova Bluetooth Serial (APK only) ──
    if (isCordovaBTAvailable()) {
      try {
        setScanStatus("Mencari perangkat Bluetooth yang sudah dipasangkan (APK)...");

        // Request permissions on Android
        if (window.cordova?.plugins?.permissions) {
          await new Promise((resolve) => {
            window.cordova.plugins.permissions.requestPermissions(
              [
                "android.permission.BLUETOOTH_CONNECT",
                "android.permission.BLUETOOTH_SCAN",
              ],
              resolve,
              resolve
            );
          });
        }

        const btList = await new Promise((resolve, reject) => {
          window.bluetoothSerial.enable(
            () => {
              window.bluetoothSerial.list(
                (list) => resolve(list),
                (err) => reject(err)
              );
            },
            () => reject("Bluetooth tidak aktif / izin ditolak")
          );
        });

        for (const dev of btList) {
          found.push({
            id: `bt-${dev.address}`,
            name: dev.name || `Bluetooth Device (${dev.address})`,
            type: "Bluetooth Serial (APK)",
            mode: "web_bluetooth",
            address: dev.address,
            connected: false,
            source: "Bluetooth Serial",
          });
        }
      } catch (err) {
        console.warn("Bluetooth Serial scan error:", err);
      }
    }

    // ── 3. Check for Capacitor Camera Scanner ──
    if (isCapacitorAvailable()) {
      found.push({
        id: "capacitor-camera-scanner",
        name: "Kamera Scanner Perangkat (Capacitor)",
        type: "Capacitor Camera / Sensor",
        mode: "capacitor_apk",
        connected: true,
        source: "Capacitor Native",
      });
    }

    setDiscoveredDevices(found);
    const savedCount = persistDiscoveredDevices(found);
    setIsScanning(false);

    if (found.length > 0) {
      setScanStatus(
        `Ditemukan ${found.length} perangkat${savedCount ? `, ${savedCount} otomatis tersimpan` : ""}.`
      );
      showSuccess(
        savedCount
          ? `${savedCount} perangkat scanner baru otomatis tersimpan.`
          : `${found.length} perangkat scanner terdeteksi.`
      );
    } else {
      setScanStatus(
        "Tidak ada perangkat ditemukan. " +
        (isWebHIDSupported()
          ? 'Klik "Pasangkan Perangkat USB/HID Baru" untuk memilih perangkat secara manual.'
          : "Browser ini tidak mendukung WebHID. Gunakan Chrome 89+ atau APK Android.")
      );
    }
  };

  /**
   * WebHID: Request new HID device pairing (opens OS device picker)
   */
  const pairNewHIDDevice = async () => {
    if (!isWebHIDSupported()) {
      showError("Browser ini tidak mendukung WebHID API. Gunakan Chrome 89+.");
      return;
    }

    try {
      setScanStatus("Membuka dialog pemilihan perangkat HID...");
      const devices = await navigator.hid.requestDevice({
        filters: [], // show all HID devices
      });

      if (devices.length > 0) {
        const dev = devices[0];
        const newDevice = {
          id: `hid-${dev.vendorId}-${dev.productId}`,
          name: dev.productName || `HID Device (${dev.vendorId}:${dev.productId})`,
          type: "USB / Bluetooth HID",
          mode: "usb_hid",
          vendorId: dev.vendorId,
          productId: dev.productId,
          connected: false,
          source: "WebHID",
        };

        setDiscoveredDevices((prev) => {
          // Replace if same ID exists
          const filtered = prev.filter((d) => d.id !== newDevice.id);
          return [...filtered, newDevice];
        });

        // Also add to saved devices
        addDiscoveredToSaved(newDevice);
        setScanStatus(`Perangkat "${newDevice.name}" berhasil dipasangkan!`);
        showSuccess(`Perangkat "${newDevice.name}" dipasangkan via WebHID!`);
      } else {
        setScanStatus("Tidak ada perangkat yang dipilih.");
      }
    } catch (err) {
      if (err.name === "NotAllowedError") {
        setScanStatus("Pengguna membatalkan pemilihan perangkat.");
      } else {
        setScanStatus(`Gagal memasangkan perangkat: ${err.message}`);
        showError(`Gagal: ${err.message}`);
      }
    }
  };

  /**
   * Web Bluetooth: Request new BT device pairing
   */
  const pairNewBluetoothDevice = async () => {
    if (!isWebBluetoothSupported()) {
      showError("Browser ini tidak mendukung Web Bluetooth API.");
      return;
    }

    try {
      setScanStatus("Membuka dialog pencarian Bluetooth...");
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: [],
      });

      const newDevice = {
        id: `webbt-${device.id}`,
        name: device.name || `Bluetooth Device (${device.id.substring(0, 8)})`,
        type: "Web Bluetooth",
        mode: "web_bluetooth",
        bluetoothId: device.id,
        connected: device.gatt?.connected || false,
        source: "Web Bluetooth",
      };

      setDiscoveredDevices((prev) => {
        const filtered = prev.filter((d) => d.id !== newDevice.id);
        return [...filtered, newDevice];
      });

      addDiscoveredToSaved(newDevice);
      setScanStatus(`Perangkat "${newDevice.name}" berhasil dipasangkan via Bluetooth!`);
      showSuccess(`Perangkat Bluetooth "${newDevice.name}" dipasangkan!`);
    } catch (err) {
      if (err.name === "NotFoundError" || err.name === "NotAllowedError") {
        setScanStatus("Pengguna membatalkan pencarian Bluetooth.");
      } else {
        setScanStatus(`Gagal memasangkan Bluetooth: ${err.message}`);
        showError(`Gagal: ${err.message}`);
      }
    }
  };

  // ─── Device Management ────────────────────────────────────────────

  /** Add a discovered device to the saved devices list */
  const addDiscoveredToSaved = (device) => {
    const addedCount = persistDiscoveredDevices([device]);
    if (addedCount === 0) {
      showInfo(`Perangkat "${device.name}" sudah tersimpan.`);
      return;
    }
    showSuccess(`Perangkat "${device.name}" ditambahkan ke daftar tersimpan!`);
  };

  /** Set a device as the active/default scanner */
  const handleSelectActiveDevice = (device) => {
    const updatedDevices = (config.savedDevices || []).map((d) => ({
      ...d,
      isDefault: d.id === device.id,
    }));

    const updatedConfig = {
      ...config,
      activeDeviceId: device.id,
      deviceName: device.name,
      mode: device.mode,
      savedDevices: updatedDevices,
    };

    handleSave(updatedConfig);
    showSuccess(`Perangkat "${device.name}" sekarang menjadi scanner aktif!`);
  };

  /** Add manual device entry */
  const handleAddManualDevice = () => {
    if (!manualDevName.trim()) {
      showError("Masukkan nama perangkat scanner!");
      return;
    }

    const modeObj = SCANNER_MODES.find((m) => m.id === manualDevMode);
    const newDev = {
      id: `manual-${Date.now()}`,
      name: manualDevName.trim(),
      type: modeObj?.name || "USB / Bluetooth HID Standard",
      mode: manualDevMode,
      status: "Ditambahkan Manual",
      isDefault: false,
      lastUsed: "Baru Ditambahkan",
      source: "Manual",
    };

    const updatedDevices = [...(config.savedDevices || []), newDev];
    const updatedConfig = { ...config, savedDevices: updatedDevices };
    handleSave(updatedConfig);

    setManualDevName("");
    setIsAddManualOpen(false);
    showSuccess(`Perangkat scanner "${newDev.name}" berhasil ditambahkan!`);
  };

  /** Delete saved device */
  const handleDeleteDevice = (id) => {
    const deletedDevice = (config.savedDevices || []).find((device) => device.id === id);
    const isActive = config.activeDeviceId === id || deletedDevice?.isDefault;
    const remaining = (config.savedDevices || []).filter((d) => d.id !== id);
    const updatedConfig = {
      ...config,
      savedDevices: remaining,
      ...(isActive
        ? {
            activeDeviceId: remaining[0]?.id || "",
            deviceName: remaining[0]?.name || "",
            mode: remaining[0]?.mode || "usb_hid",
          }
        : {}),
    };

    if (remaining.length > 0 && isActive) {
      updatedConfig.savedDevices = remaining.map((device, index) => ({
        ...device,
        isDefault: index === 0,
      }));
    }

    handleSave(updatedConfig);
    showSuccess("Perangkat scanner dihapus.");
  };

  /** Save device alias/name */
  const saveDeviceAlias = (deviceId) => {
    const trimmed = aliasInput.trim();
    if (!trimmed) return;

    const updatedDevices = (config.savedDevices || []).map((d) =>
      d.id === deviceId ? { ...d, name: trimmed } : d
    );

    let updatedConfig = { ...config, savedDevices: updatedDevices };
    if (config.activeDeviceId === deviceId) {
      updatedConfig.deviceName = trimmed;
    }

    handleSave(updatedConfig);
    setEditingDeviceId(null);
    setAliasInput("");
    showSuccess(`Nama perangkat diubah menjadi "${trimmed}"`);
  };



  // ─── Helpers ──────────────────────────────────────────────────────

  const getDeviceIcon = (mode) => {
    if (mode === "web_bluetooth") return Bluetooth;
    if (mode === "capacitor_apk") return Smartphone;
    return Usb;
  };

  const getDeviceStatusBadge = (dev) => {
    const isActive = config.activeDeviceId === dev.id || dev.isDefault;
    // Check if device is found in discovered list
    const isDiscovered = discoveredDevices.some((d) => d.id === dev.id);

    if (isActive && isDiscovered) {
      return { label: "Aktif & Terdeteksi", color: "emerald", icon: CheckCircle2 };
    }
    if (isActive) {
      return { label: "Aktif (Dipilih)", color: "teal", icon: Check };
    }
    if (isDiscovered) {
      return { label: "Terdeteksi", color: "sky", icon: Wifi };
    }
    if (dev.source === "Manual") {
      return { label: "Manual", color: "slate", icon: WifiOff };
    }
    return { label: "Tersimpan", color: "slate", icon: WifiOff };
  };

  // ─── Render ───────────────────────────────────────────────────────

  const activeSavedDevice = (config.savedDevices || []).find(
    (device) => device.id === config.activeDeviceId || device.isDefault,
  );
  const hasActiveScanner = Boolean(activeSavedDevice || config.deviceName);

  return (
    <div className="space-y-6">
      {/* Header and current status */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-11 h-11 rounded-xl bg-teal-50 ring-1 ring-teal-200 flex items-center justify-center shrink-0">
              <QrCode className="w-5 h-5 text-teal-600" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-bold text-slate-900">Scanner Barcode</h2>
                <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold ${
                  hasActiveScanner
                    ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
                    : "bg-amber-50 border border-amber-200 text-amber-700"
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${hasActiveScanner ? "bg-emerald-500" : "bg-amber-500"}`} />
                  {hasActiveScanner ? "Siap dipakai di Penjualan" : "Belum ada scanner aktif"}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Hubungkan scanner USB, Bluetooth, atau kamera APK. Perangkat yang terdeteksi akan tersimpan otomatis di perangkat ini.
              </p>
            </div>
          </div>
          <button
            onClick={scanForDevices}
            disabled={isScanning}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors text-xs font-bold disabled:opacity-50 shadow-sm shadow-teal-600/20 shrink-0"
          >
            <RefreshCw className={`w-4 h-4 ${isScanning ? "animate-spin" : ""}`} />
            {isScanning ? "Memindai perangkat..." : "Deteksi perangkat"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* Panel kiri */}
      <div className="lg:col-span-3 space-y-6">
      {/* Section 1: Scanner Discovery & Connection */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Connection methods */}
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
          <Server className="w-5 h-5 text-teal-600" />
          <div>
            <h3 className="text-sm font-bold text-slate-800">Tambah perangkat scanner</h3>
            <p className="text-xs text-slate-500">
              Pilih cara koneksi yang sesuai dengan scanner yang digunakan.
            </p>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Scan status */}
          <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-600">
            <Smartphone className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-xs font-bold text-slate-800">Status deteksi</p>
              <p className="text-xs mt-0.5">{scanStatus}</p>
            </div>
          </div>

          {/* Pair New Device Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button
              onClick={pairNewHIDDevice}
              disabled={!isWebHIDSupported()}
              className="flex items-center gap-2 p-3 bg-white border border-slate-200 rounded-xl hover:border-teal-300 hover:bg-teal-50 transition-all text-left disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <div className="p-2 bg-blue-50 rounded-lg">
                <Cable className="w-5 h-5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-slate-800">Pasangkan USB/HID Baru</p>
                <p className="text-[10px] text-slate-500">Scanner kabel USB / BT HID keyboard</p>
              </div>
            </button>
            <button
              onClick={pairNewBluetoothDevice}
              disabled={!isWebBluetoothSupported()}
              className="flex items-center gap-2 p-3 bg-white border border-slate-200 rounded-xl hover:border-teal-300 hover:bg-teal-50 transition-all text-left disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <div className="p-2 bg-indigo-50 rounded-lg">
                <Bluetooth className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-slate-800">Pasangkan Bluetooth Baru</p>
                <p className="text-[10px] text-slate-500">Scanner Bluetooth nirkabel</p>
              </div>
            </button>
            <button
              onClick={() => setIsAddManualOpen(true)}
              className="flex items-center gap-2 p-3 bg-white border border-slate-200 rounded-xl hover:border-teal-300 hover:bg-teal-50 transition-all text-left"
            >
              <div className="p-2 bg-amber-50 rounded-lg">
                <Plus className="w-5 h-5 text-amber-600" />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-slate-800">Tambah Manual</p>
                <p className="text-[10px] text-slate-500">Input nama perangkat sendiri</p>
              </div>
            </button>
          </div>

          {/* Manual Add Form */}
          {isAddManualOpen && (
            <div className="p-4 bg-amber-50/60 border border-amber-200 rounded-2xl space-y-3">
              <h4 className="text-xs font-bold text-amber-900 uppercase tracking-wide">
                Tambah Perangkat Scanner Secara Manual
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Nama Perangkat</label>
                  <input
                    type="text"
                    value={manualDevName}
                    onChange={(e) => setManualDevName(e.target.value)}
                    placeholder="Contoh: Barcode Scanner Kasir Belakang"
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-semibold text-slate-800 outline-none focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Tipe Koneksi</label>
                  <select
                    value={manualDevMode}
                    onChange={(e) => setManualDevMode(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-semibold text-slate-800 outline-none focus:border-teal-500"
                  >
                    {SCANNER_MODES.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  onClick={() => setIsAddManualOpen(false)}
                  className="px-3 py-1.5 border border-slate-300 bg-white text-slate-700 text-xs font-semibold rounded-lg hover:bg-slate-50"
                >
                  Batal
                </button>
                <button
                  onClick={handleAddManualDevice}
                  className="px-4 py-1.5 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 shadow-sm"
                >
                  Simpan Perangkat
                </button>
              </div>
            </div>
          )}

          {discoveredDevices.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800">
              <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
              <span>
                {discoveredDevices.length} perangkat terdeteksi dan sudah disimpan ke daftar scanner.
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Scanner preferences */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
        <div className="flex items-center justify-between border-b border-slate-100 pb-4">
          <div>
            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
              <SettingsIcon className="w-5 h-5 text-teal-600" />
              <span>Pengaturan scan</span>
            </h3>
            <p className="text-xs text-slate-500 mt-1">Atur respons kasir setelah barcode berhasil dibaca.</p>
          </div>
          <button
            onClick={() => handleSave()}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl text-sm transition-all shadow-sm active:scale-95"
          >
            <Save className="w-4 h-4" />
            <span>Simpan</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Auto Add to Cart */}
          <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-xl">
            <div>
              <div className="text-sm font-bold text-slate-800 mb-0.5">Otomatis Masuk Keranjang</div>
              <div className="text-xs text-slate-500">
                Langsung tambahkan produk/varian ke keranjang kasir saat barcode cocok.
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.autoAddToCart}
                onChange={(e) => setConfig({ ...config, autoAddToCart: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600" />
            </label>
          </div>

          {/* Beep Sound */}
          <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-200 rounded-xl">
            <div>
              <div className="text-sm font-bold text-slate-800 mb-0.5 flex items-center gap-1.5">
                {config.beepOnScan ? (
                  <Volume2 className="w-4 h-4 text-teal-600" />
                ) : (
                  <VolumeX className="w-4 h-4 text-slate-400" />
                )}
                <span>Suara Beep Notifikasi</span>
              </div>
              <div className="text-xs text-slate-500">
                Putar nada Beep pendek saat hasil pindaian barcode produk berhasil.
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.beepOnScan}
                onChange={(e) => setConfig({ ...config, beepOnScan: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600" />
            </label>
          </div>
        </div>

        <div className="flex items-start gap-2 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-600">
          <Info className="w-4 h-4 shrink-0 text-teal-600 mt-0.5" />
          <p>
            Scanner USB dan Bluetooth HID langsung membaca barcode sebagai input keyboard. Scanner kamera APK dibuka dari ikon QR di header Penjualan.
          </p>
        </div>
      </div>
      </div>

      {/* Panel kanan */}
      <div className="lg:col-span-2 space-y-6 lg:sticky lg:top-6 self-start">
      {/* Section 2: Saved Devices List with Active Selector */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
        <div className="flex items-center justify-between border-b border-slate-100 pb-4">
          <div>
            <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
              <Layers className="w-5 h-5 text-teal-600" />
              <span>Daftar Perangkat Scanner Tersimpan</span>
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Perangkat tersimpan di APK atau browser ini. Aktifkan satu perangkat untuk digunakan saat transaksi.
            </p>
          </div>
        </div>

        {/* Device Grid */}
        {(config.savedDevices || []).length === 0 ? (
          <div className="text-center py-10 text-slate-500 border-2 border-dashed border-slate-200 rounded-xl">
            <QrCode className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="text-sm font-semibold">Belum ada perangkat scanner tersimpan.</p>
            <p className="text-xs mt-1">
              Gunakan "Deteksi perangkat" atau salah satu opsi koneksi di atas untuk menambahkan scanner.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {(config.savedDevices || []).map((dev) => {
              const isActive = config.activeDeviceId === dev.id || dev.isDefault;
              const Icon = getDeviceIcon(dev.mode);
              const statusBadge = getDeviceStatusBadge(dev);
              const StatusIcon = statusBadge.icon;
              const isEditing = editingDeviceId === dev.id;

              return (
                <div
                  key={dev.id}
                  className={`p-4 rounded-xl border-2 transition-all relative ${
                    isActive
                      ? "border-teal-600 bg-teal-50/40 shadow-md ring-1 ring-teal-500/20"
                      : "border-slate-200 hover:border-slate-300 bg-white"
                  }`}
                >
                  {/* Device Info */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={`p-2.5 rounded-xl ${
                          isActive
                            ? "bg-teal-600 text-white"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        {isEditing ? (
                          <div className="flex items-center gap-1.5">
                            <input
                              type="text"
                              value={aliasInput}
                              onChange={(e) => setAliasInput(e.target.value)}
                              onKeyDown={(e) => e.key === "Enter" && saveDeviceAlias(dev.id)}
                              placeholder="Nama perangkat baru..."
                              className="px-2 py-1 bg-white border border-slate-300 rounded-lg text-xs font-bold text-slate-800 outline-none focus:border-teal-500 w-36"
                              autoFocus
                            />
                            <button
                              onClick={() => saveDeviceAlias(dev.id)}
                              className="p-1 bg-teal-600 text-white rounded-md hover:bg-teal-700"
                              title="Simpan"
                            >
                              <Save className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => {
                                setEditingDeviceId(null);
                                setAliasInput("");
                              }}
                              className="p-1 bg-slate-200 text-slate-600 rounded-md hover:bg-slate-300"
                              title="Batal"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <h4 className="text-sm font-bold text-slate-900 leading-tight truncate max-w-[180px]">
                              {dev.name}
                            </h4>
                            <button
                              onClick={() => {
                                setEditingDeviceId(dev.id);
                                setAliasInput(dev.name);
                              }}
                              className="p-0.5 text-slate-400 hover:text-teal-600 hover:bg-slate-100 rounded transition-colors shrink-0"
                              title="Ubah nama perangkat"
                            >
                              <Edit2 className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                        <p className="text-[10px] font-medium text-slate-500 mt-0.5 truncate max-w-[200px]">
                          {dev.type}
                          {dev.source ? ` • via ${dev.source}` : ""}
                        </p>
                      </div>
                    </div>

                    {isActive ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-500 text-white font-bold text-[11px] rounded-full shadow-sm shrink-0">
                        <Check className="w-3 h-3" /> Aktif
                      </span>
                    ) : (
                      <button
                        onClick={() => handleSelectActiveDevice(dev)}
                        className="px-2.5 py-1 bg-slate-100 hover:bg-teal-600 hover:text-white text-slate-700 font-semibold text-xs rounded-lg transition-colors shrink-0"
                      >
                        Aktifkan
                      </button>
                    )}
                  </div>

                  {/* Status + Delete row */}
                  <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <StatusIcon
                        className={`w-3.5 h-3.5 ${
                          statusBadge.color === "emerald"
                            ? "text-emerald-500"
                            : statusBadge.color === "teal"
                              ? "text-teal-500"
                              : statusBadge.color === "sky"
                                ? "text-sky-500"
                                : "text-slate-400"
                        }`}
                      />
                      <span className="font-semibold text-slate-600">
                        {statusBadge.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {dev.source && (
                        <span className="text-[10px] text-slate-400 font-mono">
                          {dev.source}
                        </span>
                      )}
                      {!isActive && (config.savedDevices || []).length > 1 && (
                        <button
                          onClick={() => handleDeleteDevice(dev.id)}
                          className="text-slate-400 hover:text-red-600 p-1 rounded-lg transition-colors"
                          title="Hapus Perangkat"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </div>
      </div>
        </div>
    </div>
  );
}
